import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Grid,
  GridItem,
  Separator,
  Text,
} from '@chakra-ui/react';
import { Control, FieldErrors, UseFormRegister } from 'react-hook-form';
import z from 'zod';

import { ActionTable, PermissionCheck } from '@/components';
import {
  FormComboBox,
  FormController,
  FormInput,
  FormTextArea,
} from '@/components/form';
import { ProcurementApi, ProjectsApi, UsersApi } from '@/state/api';
import { ProcurementType } from '@/types';
import {
  purchaseOrderStatusEnums,
  PurchaseOrderStatusType,
} from '@/types/enums';
import { OverlayDialog, PopoverLazy, toasterApi } from '@ui';

import { useAppSelector } from '@/state';
import { permissionsKeys } from '@/types/permissionsType';
import { getPurchaseOrderSupplierPdfDefaults } from '@/utils/purchaseOrderExport';
import {
  getPurchaseOrderDisplayNumber,
  getPurchaseOrderNumberError,
} from '@/utils/purchaseOrderDisplay';
import {
  buildPurchaseOrderDeleteConfirmationSchema,
  canCancelPurchaseOrder,
  canEditPurchaseOrder,
  canPermanentlyDeletePurchaseOrder,
  isPurchaseOrderInListMode,
  mustCancelBeforeDeletingPurchaseOrder,
  PurchaseOrderListMode,
} from '@/utils/purchaseOrderLifecycle';
import dayjs from 'dayjs';

type PatchPurchaseOrderType =
  ProcurementType.PurchaseOrders.PatchPurchaseOrder['body'];
const changePurchaseOrderNumberSchema = z.object({
  project_po_number: z.string().trim().min(1, {
    message: 'Purchase order number is required',
  }),
});
type ChangePurchaseOrderNumberForm = z.infer<
  typeof changePurchaseOrderNumberSchema
>;
export type PurchaseOrdersTableType =
  ProcurementType.PurchaseOrders.GetAllPurchaseOrders['response'][number];

const MANUAL_STATUS_VALUES = new Set([
  'DRAFT_PO',
  'ISSUED',
  'RECEIVED',
  'CLOSED',
]);

const exportPoSchema = z.object({
  // totals overrides
  merch: z.string().optional(),
  freight: z.string().optional(),
  tax: z.string().optional(),
  currency: z.string().optional(),
  notes_footer: z.string().optional(),

  // header/form fields
  po_date: z.string().optional(), // YYYY-MM-DD (optional)
  ship_date: z.string().optional(), // free text ("TBD")
  ship_via: z.string().optional(),

  to_company_override: z.string().optional(),
  to_address_override: z.string().optional(),
  attn: z.string().optional(),
  phone: z.string().optional(),

  ship_to_name: z.string().optional(),
  ship_to_address: z.string().optional(),

  vendor_quote: z.string().optional(),
  function_no: z.string().optional(),

  mass_sales_tax_exempt: z.boolean().optional(),
  taxable: z.boolean().optional(),
  prepaid: z.boolean().optional(),
  allowed: z.boolean().optional(),
  collect: z.boolean().optional(),

  by_name: z.string().optional(),
  by_title: z.string().optional(),

  invoice_to_email: z.string().optional(),

  primary_contact_name: z.string().optional(),
  primary_contact_email: z.string().optional(),
});
type ExportPoForm = z.infer<typeof exportPoSchema>;

type PurchaseOrdersTableProps = {
  listMode: PurchaseOrderListMode;
  handleOpenEditPurchaseOrderModal: (po: PurchaseOrdersTableType) => void;
  handleOpenInvoicesModal: (po: PurchaseOrdersTableType) => void;
};

const PurchaseOrdersTable = ({
  listMode,
  handleOpenEditPurchaseOrderModal,
  handleOpenInvoicesModal,
}: PurchaseOrdersTableProps) => {
  // Get current user
  const currentUser = useAppSelector((state) => state.auth.current.user);

  // Selected project scopes the PO list (no cross-project exposure)
  const selectedProjectId = useAppSelector(
    (state) => state.project.selectedProject?.id,
  );

  // Get all purchase orders query (scoped to the selected project)
  const {
    data: getAllPurchaseOrders,
    isLoading: isLoadingGetAllPurchaseOrders,
  } = ProcurementApi.Api.useGetAllPurchaseOrdersQuery(
    {
      query: {
        project_id: selectedProjectId ?? undefined,
        cancelled: listMode === 'cancelled',
      },
    },
    { skip: !selectedProjectId },
  );

  // Patch purchase order mutation
  const [patchPurchaseOrderMutation] =
    ProcurementApi.Api.usePatchPurchaseOrderMutation();

  // Cancel purchase order mutation
  const [postPurchaseOrderCancelMutation] =
    ProcurementApi.Api.usePostPurchaseOrderCancelMutation();

  const [postPurchaseOrderRestoreMutation] =
    ProcurementApi.Api.usePostPurchaseOrderRestoreMutation();

  const [deletePurchaseOrderMutation] =
    ProcurementApi.Api.useDeletePurchaseOrderMutation();

  // Post purchase order status mutation
  const [postPurchaseOrderStatusMutation] =
    ProcurementApi.Api.usePostPurchaseOrderStatusMutation();

  // Mark / set / edit received date mutation
  const [postPurchaseOrderReceiveMutation] =
    ProcurementApi.Api.usePostPurchaseOrderReceiveMutation();

  // Clear received state (un-mark) mutation
  const [deletePurchaseOrderReceiveMutation] =
    ProcurementApi.Api.useDeletePurchaseOrderReceiveMutation();

  // NEW: Export PO PDF mutation (blob)
  const [exportPurchaseOrderPdfMutation] =
    ProcurementApi.Api.useExportPurchaseOrderPdfMutation();

  // Get suppliers query
  const { data: getAllSuppliers, isLoading: isLoadingGetAllSuppliers } =
    ProcurementApi.Api.useGetAllSuppliersQuery({});

  // Get projects query
  const { data: getAllProjects, isLoading: isLoadingGetAllProjects } =
    ProjectsApi.Api.useGetAllProjectsQuery();

  // Get all users query
  const { data: getAllUserNames, isLoading: isLoadingGetAllUserNames } =
    UsersApi.Api.useGetAllUserNamesQuery();

  const purchaseOrders = (getAllPurchaseOrders ?? []).filter((po) =>
    isPurchaseOrderInListMode(po.status, listMode),
  );

  const getApiErrorDetail = (error: any, fallback: string) =>
    error?.data?.detail || fallback;

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportPoPdf = (purchaseOrder: PurchaseOrdersTableType) => {
    const displayNumber = getPurchaseOrderDisplayNumber(purchaseOrder);
    const supplierDefaults = getPurchaseOrderSupplierPdfDefaults(
      purchaseOrder,
      getAllSuppliers,
    );
    OverlayDialog.open('exportPoPdf', {
      overlayId: 'exportPoPdf',
      title: `Export PO PDF - ${displayNumber}`,
      description: 'Enter totals before exporting (optional).',
      content: (
        <FormController
          size="lg"
          formSchema={exportPoSchema}
          defaultValues={{
            merch: '',
            freight: '',
            tax: '',
            currency: 'USD',
            notes_footer: '',

            po_date: dayjs().format('YYYY-MM-DD'),
            ship_date: 'TBD',
            ship_via: 'Your Truck',

            to_company_override: supplierDefaults.to_company_override,
            to_address_override: supplierDefaults.to_address_override,
            attn: '',
            phone: supplierDefaults.phone,

            ship_to_name: '',
            ship_to_address: '',

            vendor_quote:
              purchaseOrder.supplier_reference?.trim() || 'Attached',
            function_no: purchaseOrder.function_number ?? '',

            mass_sales_tax_exempt: false,
            taxable: false,
            prepaid: false,
            allowed: false,
            collect: false,

            by_name: '',
            by_title: 'Project Manager',

            invoice_to_email: 'jlangsmead@karasglass.com',

            primary_contact_name: '',
            primary_contact_email: '',
          }}
          onFormSubmit={async (formData: ExportPoForm) => {
            try {
              const body = {
                merch: formData.merch ? Number(formData.merch) : null,
                freight: formData.freight ? Number(formData.freight) : null,
                tax: formData.tax ? Number(formData.tax) : null,

                currency: formData.currency || null,
                notes_footer: formData.notes_footer || null,

                po_date: formData.po_date || null,
                ship_date: formData.ship_date || null,
                ship_via: formData.ship_via || null,

                to_company_override: formData.to_company_override || null,
                to_address_override: formData.to_address_override || null,
                attn: formData.attn || null,
                phone: formData.phone || null,

                ship_to_name: formData.ship_to_name || null,
                ship_to_address: formData.ship_to_address || null,

                vendor_quote: formData.vendor_quote || null,
                function_no: formData.function_no || null,

                mass_sales_tax_exempt: !!formData.mass_sales_tax_exempt,
                taxable: !!formData.taxable,
                prepaid: !!formData.prepaid,
                allowed: !!formData.allowed,
                collect: !!formData.collect,

                by_name: formData.by_name || null,
                by_title: formData.by_title || null,

                invoice_to_email: formData.invoice_to_email || null,

                primary_contact_name: formData.primary_contact_name || null,
                primary_contact_email: formData.primary_contact_email || null,
              };

              const pdfBlob = (await exportPurchaseOrderPdfMutation({
                params: { po_id: purchaseOrder.id },
                body,
              }).unwrap()) as Blob;

              downloadBlob(pdfBlob, `PO_${displayNumber}.pdf`);

              toasterApi({
                type: 'success',
                status: 200,
                code: 'Success',
                message: 'PDF exported successfully',
              });
            } catch {
              toasterApi({
                type: 'error',
                status: 500,
                code: 'Error',
                message: 'Could not export PDF',
              });
            } finally {
              OverlayDialog.close('exportPoPdf');
            }
          }}
        >
          {({
            register,
            control,
            errors,
          }: {
            register: UseFormRegister<ExportPoForm>;
            control: Control<ExportPoForm>;
            errors: FieldErrors<ExportPoForm>;
          }) => (
            <Flex flexDirection="column" gap={4} width="full">
              {/* Totals */}
              <Text fontWeight="bold">Totals (optional)</Text>
              <Flex gap={3} flexWrap="wrap">
                <Box width="160px">
                  <FormInput
                    width="160px"
                    register={register}
                    control={control}
                    errors={errors}
                    label="Merch"
                    id="merch"
                    type="number"
                    showLabelAtTop
                    min={0}
                  />
                </Box>
                <Box width="160px">
                  <FormInput
                    width="160px"
                    register={register}
                    control={control}
                    errors={errors}
                    label="Freight"
                    id="freight"
                    type="number"
                    showLabelAtTop
                    min={0}
                  />
                </Box>
                <Box width="160px">
                  <FormInput
                    width="160px"
                    register={register}
                    control={control}
                    errors={errors}
                    label="Tax"
                    id="tax"
                    type="number"
                    showLabelAtTop
                    min={0}
                  />
                </Box>
                <Box width="160px">
                  <FormInput
                    width="160px"
                    register={register}
                    control={control}
                    errors={errors}
                    label="Currency"
                    id="currency"
                    showLabelAtTop
                  />
                </Box>
              </Flex>

              <FormInput
                register={register}
                control={control}
                errors={errors}
                label="PO Date"
                id="po_date"
                type="date"
                showLabelAtTop
              />

              <Flex gap={3} flexWrap="wrap">
                <Box width="200px">
                  <FormInput
                    width="200px"
                    register={register}
                    control={control}
                    errors={errors}
                    label="Ship Date"
                    id="ship_date"
                    showLabelAtTop
                  />
                </Box>
                <Box width="200px">
                  <FormInput
                    width="200px"
                    register={register}
                    control={control}
                    errors={errors}
                    label="Ship Via"
                    id="ship_via"
                    showLabelAtTop
                  />
                </Box>
              </Flex>

              <Separator />

              {/* Supplier overrides */}
              <Text fontWeight="bold">Supplier</Text>
              <FormInput
                register={register}
                control={control}
                errors={errors}
                label="Supplier"
                id="to_company_override"
                showLabelAtTop
              />
              <FormTextArea
                register={register}
                control={control}
                errors={errors}
                label="Supplier Address"
                id="to_address_override"
                showLabelAtTop
                minH="4lh"
              />
              <Flex gap={3} flexWrap="wrap">
                <Box width="200px">
                  <FormInput
                    width="200px"
                    register={register}
                    control={control}
                    errors={errors}
                    label="ATTN"
                    id="attn"
                    showLabelAtTop
                  />
                </Box>
                <Box width="200px">
                  <FormInput
                    width="200px"
                    register={register}
                    control={control}
                    errors={errors}
                    label="Phone"
                    id="phone"
                    showLabelAtTop
                  />
                </Box>
              </Flex>

              <Separator />

              {/* Ship To overrides */}
              <Text fontWeight="bold">Ship To (optional overrides)</Text>
              <FormInput
                register={register}
                control={control}
                errors={errors}
                label="Ship To Name"
                id="ship_to_name"
                showLabelAtTop
              />
              <FormTextArea
                register={register}
                control={control}
                errors={errors}
                label="Ship To Address"
                id="ship_to_address"
                showLabelAtTop
                minH="4lh"
              />

              <Separator />

              {/* Reference / accounting */}
              <Text fontWeight="bold">References</Text>
              <Flex gap={3} flexWrap="wrap">
                <Box width="200px">
                  <FormInput
                    width="200px"
                    register={register}
                    control={control}
                    errors={errors}
                    label="Vendor Quote"
                    id="vendor_quote"
                    showLabelAtTop
                  />
                </Box>
                <Box width="140px">
                  <FormInput
                    width="140px"
                    register={register}
                    control={control}
                    errors={errors}
                    label="Function #"
                    id="function_no"
                    showLabelAtTop
                  />
                </Box>
              </Flex>

              <Separator />

              {/* Flags */}
              <Text fontWeight="bold">Flags</Text>
              <Flex gap={4} flexWrap="wrap">
                <label>
                  <input
                    type="checkbox"
                    {...register('mass_sales_tax_exempt')}
                  />{' '}
                  Mass Sales Tax Exempt
                </label>
                <label>
                  <input type="checkbox" {...register('taxable')} /> Taxable
                </label>
                <label>
                  <input type="checkbox" {...register('prepaid')} /> Prepaid
                </label>
                <label>
                  <input type="checkbox" {...register('allowed')} /> Allowed
                </label>
                <label>
                  <input type="checkbox" {...register('collect')} /> Collect
                </label>
              </Flex>

              <Separator />

              {/* Signature / invoice */}
              <Text fontWeight="bold">Signature / Invoice</Text>
              <Flex gap={3} flexWrap="wrap">
                <Box width="240px">
                  <FormInput
                    width="240px"
                    register={register}
                    control={control}
                    errors={errors}
                    label="By"
                    id="by_name"
                    showLabelAtTop
                  />
                </Box>
                <Box width="240px">
                  <FormInput
                    width="240px"
                    register={register}
                    control={control}
                    errors={errors}
                    label="Title"
                    id="by_title"
                    showLabelAtTop
                  />
                </Box>
              </Flex>

              <FormInput
                register={register}
                control={control}
                errors={errors}
                label="Invoice To Email"
                id="invoice_to_email"
                showLabelAtTop
              />

              <Separator />

              {/* Footer notes */}
              <FormTextArea
                register={register}
                control={control}
                errors={errors}
                label="Footer Notes"
                id="notes_footer"
                showLabelAtTop
                minH="4lh"
              />

              {/* Page 2 contact */}
              <Text fontWeight="bold">Page 2 Contact (optional)</Text>
              <Flex gap={3} flexWrap="wrap">
                <Box width="260px">
                  <FormInput
                    width="260px"
                    register={register}
                    control={control}
                    errors={errors}
                    label="Primary Contact Name"
                    id="primary_contact_name"
                    showLabelAtTop
                  />
                </Box>
                <Box width="260px">
                  <FormInput
                    width="260px"
                    register={register}
                    control={control}
                    errors={errors}
                    label="Primary Contact Email"
                    id="primary_contact_email"
                    showLabelAtTop
                  />
                </Box>
              </Flex>
            </Flex>
          )}
        </FormController>
      ),
    });
  };

  const handleEditPurchaseOrder = (purchaseOrder: PurchaseOrdersTableType) => {
    OverlayDialog.open('editPurchaseOrder', {
      overlayId: 'editPurchaseOrder',
      title: 'Edit purchase order',
      description: 'Update this draft purchase order.',
      content: (
        <FormController
          size="lg"
          formSchema={ProcurementType.PurchaseOrders.patchPurchaseOrder.body}
          defaultValues={{
            project_po_number: purchaseOrder?.project_po_number ?? '',
            title: purchaseOrder?.title ?? '',
            function_number: purchaseOrder?.function_number ?? '',
            expected_delivery_date:
              purchaseOrder?.expected_delivery_date &&
              dayjs(purchaseOrder?.expected_delivery_date).isValid()
                ? dayjs(purchaseOrder?.expected_delivery_date).format(
                    'YYYY-MM-DD',
                  )
                : '',
            supplier: purchaseOrder?.supplier ?? '',
            shipping_address: purchaseOrder?.shipping_address ?? '',
          }}
          onFormSubmit={async (formData) => {
            const projectPoNumber = formData?.project_po_number?.trim();
            const title = formData?.title?.trim() || null;
            const functionNumber = formData?.function_number?.trim() || null;
            const patchPayload = {
              ...(projectPoNumber &&
                projectPoNumber !== purchaseOrder?.project_po_number && {
                  project_po_number: projectPoNumber,
                }),
              ...(title !== purchaseOrder?.title && { title }),
              ...(functionNumber !== purchaseOrder?.function_number && {
                function_number: functionNumber,
              }),
              ...(formData?.supplier && { supplier: formData?.supplier }),
              ...(formData?.shipping_address && {
                shipping_address: formData?.shipping_address,
              }),
              ...(dayjs(formData?.expected_delivery_date).isValid() && {
                expected_delivery_date: formData?.expected_delivery_date,
              }),
            };

            try {
              await patchPurchaseOrderMutation({
                body: patchPayload,
                params: {
                  id: purchaseOrder?.id,
                },
              }).unwrap();
              toasterApi({
                type: 'success',
                status: 200,
                code: 'Success',
                message: 'Purchase order updated successfully',
              });
              OverlayDialog.close('editPurchaseOrder');
            } catch (error) {
              toasterApi({
                type: 'error',
                status: 400,
                code: 'Error',
                message: getPurchaseOrderNumberError(
                  error,
                  'Could not update purchase order',
                ),
              });
            }
          }}
        >
          {({
            register,
            control,
            errors,
          }: {
            register: UseFormRegister<PatchPurchaseOrderType>;
            control: Control<PatchPurchaseOrderType>;
            errors: FieldErrors<PatchPurchaseOrderType>;
          }) => {
            return (
              <Flex flexDirection="column" gapY={3}>
                <Flex gapX={2} flexWrap="wrap">
                  <Box width="240px">
                    <FormInput
                      width="240px"
                      register={register}
                      control={control}
                      errors={errors}
                      id="project_po_number"
                      label="Purchase Order Number"
                      helperText="Use an unused suffix or the full matching project number."
                      showLabelAtTop
                    />
                  </Box>
                  <Box width="240px">
                    <FormInput
                      width="240px"
                      register={register}
                      control={control}
                      errors={errors}
                      id="title"
                      label="Title"
                      showLabelAtTop
                    />
                  </Box>
                  <Box width="200px">
                    <FormInput
                      width="200px"
                      register={register}
                      control={control}
                      errors={errors}
                      id="function_number"
                      label="Function Number"
                      showLabelAtTop
                    />
                  </Box>
                </Flex>
                <Flex gapX={2}>
                  {/* Supplier */}
                  <Box width="200px">
                    <FormComboBox
                      width="200px"
                      register={register}
                      control={control}
                      errors={errors}
                      id="supplier"
                      label="Supplier"
                      keyForDisplay="name"
                      keyForValue="id"
                      items={getAllSuppliers ?? []}
                      showLabelAtTop
                      required
                      clearable={false}
                      disabled={isLoadingGetAllSuppliers}
                      isLoading={isLoadingGetAllSuppliers}
                      helperText="Type any part of the company name, then select an existing supplier."
                    />
                  </Box>

                  {/* Expected delivery date */}
                  <Box width="200px">
                    <FormInput
                      width="200px"
                      register={register}
                      control={control}
                      errors={errors}
                      label="Expected Delivery Date"
                      id="expected_delivery_date"
                      type="date"
                      showLabelAtTop
                    />
                  </Box>

                  {/* Shipping address */}
                  <FormInput
                    register={register}
                    control={control}
                    errors={errors}
                    label="Shipping Address"
                    id="shipping_address"
                    showLabelAtTop
                  />
                </Flex>
              </Flex>
            );
          }}
        </FormController>
      ),
    });
  };

  const handleChangePurchaseOrderNumber = (
    purchaseOrder: PurchaseOrdersTableType,
  ) => {
    OverlayDialog.open('changePurchaseOrderNumber', {
      overlayId: 'changePurchaseOrderNumber',
      title: 'Change purchase order number',
      description:
        'The new project purchase order number must not be used by another purchase order.',
      content: (
        <FormController
          size="lg"
          formSchema={changePurchaseOrderNumberSchema}
          defaultValues={{
            project_po_number: purchaseOrder.project_po_number ?? '',
          }}
          onFormSubmit={async (formData: ChangePurchaseOrderNumberForm) => {
            try {
              await patchPurchaseOrderMutation({
                body: {
                  project_po_number: formData.project_po_number.trim(),
                },
                params: { id: purchaseOrder.id },
              }).unwrap();
              toasterApi({
                type: 'success',
                status: 200,
                code: 'Success',
                message: 'Purchase order number updated successfully',
              });
              OverlayDialog.close('changePurchaseOrderNumber');
            } catch (error) {
              toasterApi({
                type: 'error',
                status: 400,
                code: 'Error',
                message: getPurchaseOrderNumberError(
                  error,
                  'Could not update purchase order number',
                ),
              });
            }
          }}
        >
          {({ register, control, errors }) => (
            <FormInput
              register={register}
              control={control}
              errors={errors}
              id="project_po_number"
              label="Purchase Order Number"
              helperText="Enter an unused numeric suffix or the full matching project number."
              showLabelAtTop
              required
            />
          )}
        </FormController>
      ),
    });
  };

  const handleCancelPurchaseOrder = (
    purchaseOrder: PurchaseOrdersTableType,
  ) => {
    OverlayDialog.open('cancelPurchaseOrder', {
      overlayId: 'cancelPurchaseOrder',
      title: 'Cancel purchase order',
      content: (
        <Flex flexDirection="column">
          <Text>
            Are you sure you want to cancel purchase order{' '}
            {getPurchaseOrderDisplayNumber(purchaseOrder)}?
          </Text>
          <Text mt={2} color="fg.muted" fontSize="sm">
            Cancelling preserves the purchase order number and all details. You
            can restore it to Draft later if it needs to be edited.
          </Text>
        </Flex>
      ),
      handleConfirm: () => {
        postPurchaseOrderCancelMutation({
          params: {
            po_id: purchaseOrder.id,
          },
        })
          .unwrap()
          .then(() => {
            toasterApi({
              type: 'success',
              status: 200,
              code: 'Success',
              message: 'Purchase order cancelled successfully',
            });
          })
          .catch((error) => {
            toasterApi({
              type: 'error',
              status: error?.status || 500,
              code: 'Error',
              message: getApiErrorDetail(
                error,
                'Could not cancel purchase order',
              ),
            });
          })
          .finally(() => {
            OverlayDialog.close('cancelPurchaseOrder');
          });
      },
    });
  };

  const handleRestorePurchaseOrder = (
    purchaseOrder: PurchaseOrdersTableType,
  ) => {
    const displayNumber = getPurchaseOrderDisplayNumber(purchaseOrder);
    OverlayDialog.open('restorePurchaseOrder', {
      overlayId: 'restorePurchaseOrder',
      title: 'Restore purchase order to Draft',
      content: (
        <Flex flexDirection="column" gapY={2}>
          <Text>Restore purchase order {displayNumber} to Draft?</Text>
          <Text color="fg.muted" fontSize="sm">
            Its number and details will stay the same, and the header and lines
            will become editable again.
          </Text>
        </Flex>
      ),
      handleConfirm: () => {
        postPurchaseOrderRestoreMutation({
          params: { po_id: purchaseOrder.id },
        })
          .unwrap()
          .then(() => {
            toasterApi({
              type: 'success',
              status: 200,
              code: 'Success',
              message: 'Purchase order restored to Draft',
            });
          })
          .catch((error) => {
            toasterApi({
              type: 'error',
              status: error?.status || 500,
              code: 'Error',
              message: getApiErrorDetail(
                error,
                'Could not restore purchase order',
              ),
            });
          })
          .finally(() => {
            OverlayDialog.close('restorePurchaseOrder');
          });
      },
    });
  };

  const handleDeletePurchaseOrder = (
    purchaseOrder: PurchaseOrdersTableType,
  ) => {
    const confirmationNumber =
      purchaseOrder.project_po_number ||
      getPurchaseOrderDisplayNumber(purchaseOrder);
    const deleteSchema =
      buildPurchaseOrderDeleteConfirmationSchema(confirmationNumber);
    type DeletePurchaseOrderForm = z.infer<typeof deleteSchema>;

    OverlayDialog.open('deletePurchaseOrder', {
      overlayId: 'deletePurchaseOrder',
      title: 'Permanently delete purchase order',
      description: `This permanently removes ${confirmationNumber} from the system.`,
      content: (
        <FormController
          size="md"
          formSchema={deleteSchema}
          defaultValues={{ confirmation: '' }}
          onFormSubmit={async () => {
            try {
              await deletePurchaseOrderMutation({
                params: { id: purchaseOrder.id },
              }).unwrap();
              toasterApi({
                type: 'success',
                status: 204,
                code: 'Success',
                message: 'Purchase order permanently deleted',
              });
            } catch (error: any) {
              toasterApi({
                type: 'error',
                status: error?.status || 500,
                code: 'Error',
                message: getApiErrorDetail(
                  error,
                  'Could not permanently delete purchase order',
                ),
              });
            } finally {
              OverlayDialog.close('deletePurchaseOrder');
            }
          }}
        >
          {({
            register,
            control,
            errors,
          }: {
            register: UseFormRegister<DeletePurchaseOrderForm>;
            control: Control<DeletePurchaseOrderForm>;
            errors: FieldErrors<DeletePurchaseOrderForm>;
          }) => (
            <Flex flexDirection="column" gapY={3}>
              <Text color="fg.error" fontWeight="semibold">
                This cannot be undone. The PO header, lines, and invoices will
                be removed. Other purchase orders will not be renumbered.
              </Text>
              <Text fontSize="sm">
                Type <b>{confirmationNumber}</b> to confirm.
              </Text>
              <FormInput
                register={register}
                control={control}
                errors={errors}
                id="confirmation"
                label="Purchase Order Number"
                showLabelAtTop
              />
            </Flex>
          )}
        </FormController>
      ),
    });
  };

  // Plain status change (no received-date side effects).
  const applyStatusChange = (
    purchaseOrder: PurchaseOrdersTableType,
    newStatus: PurchaseOrdersTableType['status'],
  ) => {
    postPurchaseOrderStatusMutation({
      body: {
        status: newStatus,
      },
      params: {
        po_id: purchaseOrder?.id,
      },
    })
      .unwrap()
      .then(() => {
        toasterApi({
          type: 'success',
          status: 200,
          code: 'Success',
          message: 'Purchase order status changed successfully',
        });
      })
      .catch(() => {
        toasterApi({
          type: 'error',
          status: 500,
          code: 'Error',
          message: 'Could not change purchase order status',
        });
      })
      .finally(() => {
        OverlayDialog.close('postPurchaseOrderStatus');
      });
  };

  // Moving a received PO back to Issued/Draft must clear the recorded received
  // date (otherwise the PO would show a received date while no longer being
  // "received"). We confirm, clear via the receive endpoint (which reverts to
  // ISSUED), then set the final target status when it isn't ISSUED.
  const handleClearReceivedToStatus = (
    purchaseOrder: PurchaseOrdersTableType,
    targetStatus: PurchaseOrdersTableType['status'],
  ) => {
    OverlayDialog.close('postPurchaseOrderStatus');
    OverlayDialog.open('clearReceivedConfirm', {
      overlayId: 'clearReceivedConfirm',
      title: 'Change status away from Received',
      content: (
        <Flex flexDirection="column">
          <Text>
            This PO already has a received date. Changing the status away from
            Received will clear the received date and may make the PO overdue
            again. Continue?
          </Text>
        </Flex>
      ),
      handleConfirm: () => {
        deletePurchaseOrderReceiveMutation({
          params: { po_id: purchaseOrder?.id },
        })
          .unwrap()
          .then(async () => {
            // clear_received reverts RECEIVED -> ISSUED. If the user picked a
            // different target (e.g. DRAFT_PO), apply it as a follow-up.
            if (targetStatus !== 'ISSUED') {
              await postPurchaseOrderStatusMutation({
                body: { status: targetStatus },
                params: { po_id: purchaseOrder?.id },
              }).unwrap();
            }
            toasterApi({
              type: 'success',
              status: 200,
              code: 'Success',
              message: 'Received date cleared and status updated',
            });
          })
          .catch(() => {
            toasterApi({
              type: 'error',
              status: 500,
              code: 'Error',
              message: 'Could not change purchase order status',
            });
          })
          .finally(() => {
            OverlayDialog.close('clearReceivedConfirm');
          });
      },
    });
  };

  const handlePurchaseOrderStatus = (
    purchaseOrder: PurchaseOrdersTableType,
  ) => {
    OverlayDialog.open('postPurchaseOrderStatus', {
      overlayId: 'postPurchaseOrderStatus',
      title: 'Change purchase order status',
      description: 'Change purchase order status?',
      content: (
        <Flex gapX={4}>
          {purchaseOrderStatusEnums
            .filter(
              (status) =>
                MANUAL_STATUS_VALUES.has(status.value) &&
                (status?.value !== 'DRAFT_PO' ||
                  purchaseOrder?.status === 'ISSUED'),
            )
            // Keep "Received" available even when the PO is already received so
            // it can be used to edit the received date; hide every other
            // current-status button.
            ?.filter(
              (status) =>
                status?.value !== purchaseOrder?.status ||
                status?.value === 'RECEIVED',
            )
            ?.map((status) => {
              const buttonColor = purchaseOrderStatusColor({
                type: 'button',
                status: purchaseOrderStatusEnums?.find(
                  (item) => item?.value === status?.value,
                )?.label as PurchaseOrderStatusType,
              });

              const isReceivedTarget = status?.value === 'RECEIVED';
              const isClearingReceived =
                !isReceivedTarget &&
                Boolean(purchaseOrder?.received_date) &&
                (status?.value === 'ISSUED' || status?.value === 'DRAFT_PO');

              return (
                <Button
                  textTransform="capitalize"
                  bg={buttonColor}
                  onClick={() => {
                    // Status "Received" routes through the exact same
                    // Mark/Edit Received dialog + receive endpoint.
                    if (isReceivedTarget) {
                      OverlayDialog.close('postPurchaseOrderStatus');
                      handleReceivePurchaseOrder(purchaseOrder);
                      return;
                    }

                    // Leaving a received state must clear the received date.
                    if (isClearingReceived) {
                      handleClearReceivedToStatus(purchaseOrder, status?.value);
                      return;
                    }

                    applyStatusChange(purchaseOrder, status?.value);
                  }}
                >
                  {status?.label.split('_')?.join(' ')}
                </Button>
              );
            })}
        </Flex>
      ),
    });
  };

  const handleReceivePurchaseOrder = (
    purchaseOrder: PurchaseOrdersTableType,
  ) => {
    const alreadyReceived = Boolean(purchaseOrder?.received_date);
    OverlayDialog.open('receivePurchaseOrder', {
      overlayId: 'receivePurchaseOrder',
      title: alreadyReceived
        ? `Edit received date - ${getPurchaseOrderDisplayNumber(purchaseOrder)}`
        : `Mark received - ${getPurchaseOrderDisplayNumber(purchaseOrder)}`,
      description: alreadyReceived
        ? 'Update the date these items were received.'
        : 'Record the date these items were received. This clears any overdue flag.',
      content: (
        <FormController
          size="lg"
          formSchema={receivePoSchema}
          defaultValues={{
            received_date:
              purchaseOrder?.received_date &&
              dayjs(purchaseOrder?.received_date).isValid()
                ? dayjs.utc(purchaseOrder?.received_date).format('YYYY-MM-DD')
                : dayjs().format('YYYY-MM-DD'),
            received_notes: purchaseOrder?.received_notes ?? '',
          }}
          onFormSubmit={async (formData: ReceivePoForm) => {
            postPurchaseOrderReceiveMutation({
              body: {
                received_date: formData?.received_date,
                received_notes: formData?.received_notes || undefined,
              },
              params: { po_id: purchaseOrder?.id },
            })
              .unwrap()
              .then(() => {
                toasterApi({
                  type: 'success',
                  status: 200,
                  code: 'Success',
                  message: alreadyReceived
                    ? 'Received date updated successfully'
                    : 'Purchase order marked as received',
                });
              })
              .catch(() => {
                toasterApi({
                  type: 'error',
                  status: 500,
                  code: 'Error',
                  message: 'Could not set received date',
                });
              })
              .finally(() => {
                OverlayDialog.close('receivePurchaseOrder');
              });
          }}
        >
          {({
            register,
            control,
            errors,
          }: {
            register: UseFormRegister<ReceivePoForm>;
            control: Control<ReceivePoForm>;
            errors: FieldErrors<ReceivePoForm>;
          }) => (
            <Flex flexDirection="column" gapY={3}>
              <Box width="240px">
                <FormInput
                  width="240px"
                  register={register}
                  control={control}
                  errors={errors}
                  label="Received Date"
                  id="received_date"
                  type="date"
                  showLabelAtTop
                />
              </Box>
              <FormTextArea
                register={register}
                control={control}
                errors={errors}
                label="Notes (optional)"
                id="received_notes"
                showLabelAtTop
                minH="4lh"
              />
            </Flex>
          )}
        </FormController>
      ),
    });
  };

  const handleSubmitPo = ({
    purchaseOrder,
    actionButtonType,
  }: {
    purchaseOrder: PurchaseOrdersTableType;
    actionButtonType: ActionButtonType;
  }) => {
    if (actionButtonType === 'editPO') handleEditPurchaseOrder(purchaseOrder);
    if (actionButtonType === 'changePONumber')
      handleChangePurchaseOrderNumber(purchaseOrder);
    if (actionButtonType === 'editPOLines')
      handleOpenEditPurchaseOrderModal(purchaseOrder);

    if (actionButtonType === 'cancel') {
      if (!canCancelPurchaseOrder(purchaseOrder.status)) return;
      handleCancelPurchaseOrder(purchaseOrder);
    }

    if (actionButtonType === 'status') handlePurchaseOrderStatus(purchaseOrder);
    if (actionButtonType === 'restore')
      handleRestorePurchaseOrder(purchaseOrder);
    if (actionButtonType === 'delete') handleDeletePurchaseOrder(purchaseOrder);
    if (actionButtonType === 'exportPdf') handleExportPoPdf(purchaseOrder);
    if (actionButtonType === 'viewInvoices')
      handleOpenInvoicesModal(purchaseOrder);
  };

  const filterConfigComponents: ActionTable.FilterConfigComponentType<PurchaseOrdersTableType>[] =
    [
      {
        filterComponent: 'input',
        filterComponentLabel: 'Purchase Order Number',
        filterConfigItems: [
          {
            colKeys: ['project_po_number'],
            value: '',
            operator: 'contains',
          },
        ],
      },
      {
        filterComponent: 'combobox',
        filterComponentLabel: 'Created By',
        filterConfigItems: getAllUserNames?.map((user) => ({
          colKeys: ['created_by'],
          label: user?.display_name,
          value: user?.id as number,
          operator: 'contains',
        })),
      },
      {
        filterComponent: 'combobox',
        filterComponentLabel: 'Supplier',
        filterConfigItems: getAllSuppliers?.map((supplier) => ({
          colKeys: ['supplier'],
          label: supplier?.name,
          value: supplier?.id as number,
          operator: 'contains',
        })),
      },
      {
        filterComponent: 'select',
        filterComponentLabel: 'Project',
        filterConfigItems: getAllProjects?.map((project) => ({
          colKeys: ['project'],
          label: project?.name,
          value: project?.id as number,
          operator: 'contains',
        })),
      },
      {
        filterComponent: 'select',
        filterComponentLabel: 'Status',
        filterConfigItems: purchaseOrderStatusEnums?.map((status) => ({
          colKeys: ['status'],
          label: status?.label,
          value: status?.value,
          operator: 'contains',
        })),
      },
    ].filter(
      (config) =>
        listMode === 'active' || config.filterComponentLabel !== 'Status',
    );

  const renderCell = (
    props: ActionTable.RenderCellType<
      PurchaseOrdersTableType,
      { actions: null }
    >,
  ) => {
    const { colKey, cellValue, rowValue } = props;

    switch (colKey) {
      case 'supplier':
        return (
          <Text>
            {getAllSuppliers?.find((s) => s.id === cellValue)?.name ??
              cellValue}
          </Text>
        );
      case 'project':
        return (
          <Text>
            {getAllProjects?.find((s) => s.id === cellValue)?.name ?? cellValue}
          </Text>
        );
      case 'release_numbers':
        return (
          <Text>
            {cellValue?.length ? cellValue.join(', ') : 'Project-level'}
          </Text>
        );
      case 'created_by':
        return (
          <Text>
            {getAllUserNames?.find((s) => s.id === cellValue)?.display_name ??
              cellValue}
          </Text>
        );
      case 'project_po_number':
      case 'po_number':
        return (
          <Text whiteSpace="break-spaces" wordBreak="break-all">
            {cellValue}
          </Text>
        );
      case 'created_at':
        return <Text>{dayjs(cellValue).format('DD MMM YYYY, hh:mm a')}</Text>;
      case 'expected_delivery_date': {
        const isDateValid = cellValue && dayjs(cellValue).isValid();
        return (
          <Text>
            {isDateValid
              ? dayjs.utc(cellValue).format('DD MMM YYYY')
              : 'No date set'}
          </Text>
        );
      }
      case 'status': {
        const statusLabel = purchaseOrderStatusEnums?.find(
          (s) => s.value === cellValue,
        )?.label;

        return (
          <Text
            color={`${purchaseOrderStatusColor({
              type: 'text',
              status: statusLabel as PurchaseOrderStatusType,
            })}/95`}
          >
            {statusLabel}
          </Text>
        );
      }
      case 'received_date': {
        const item = rowValue;
        if (item?.received_date && dayjs(item.received_date).isValid()) {
          return (
            <Flex flexDirection="column" gapY={1}>
              <Text>{dayjs.utc(item.received_date).format('DD MMM YYYY')}</Text>
              {item?.received_by_name && (
                <Text fontSize="xs" color="fg.subtle">
                  by {item.received_by_name}
                </Text>
              )}
            </Flex>
          );
        }
        if (isPoOverdue(item)) {
          return (
            <Badge colorPalette="red" variant="solid">
              Overdue
            </Badge>
          );
        }
        return <Text color="fg.subtle">Not received</Text>;
      }
      case 'actions': {
        const item = rowValue;

        if (item?.status === 'CANCELLED') {
          return (
            <Grid templateColumns="repeat(2, 1fr)" gap={2}>
              <PermissionCheck
                permissions={permissionsKeys.change_purchaseorder}
              >
                <GridItem>
                  <Button
                    width="full"
                    size="xs"
                    variant="outline"
                    onClick={() =>
                      handleSubmitPo({
                        purchaseOrder: item,
                        actionButtonType: 'changePONumber',
                      })
                    }
                  >
                    Change PO Number
                  </Button>
                </GridItem>
                <GridItem>
                  <Button
                    width="full"
                    size="xs"
                    bg="blue.700"
                    onClick={() =>
                      handleSubmitPo({
                        purchaseOrder: item,
                        actionButtonType: 'restore',
                      })
                    }
                  >
                    Restore to Draft
                  </Button>
                </GridItem>
              </PermissionCheck>
              <PermissionCheck
                permissions={permissionsKeys.delete_purchaseorder}
              >
                <GridItem>
                  <Button
                    width="full"
                    size="xs"
                    bg="red.800"
                    onClick={() =>
                      handleSubmitPo({
                        purchaseOrder: item,
                        actionButtonType: 'delete',
                      })
                    }
                  >
                    Permanently Delete
                  </Button>
                </GridItem>
              </PermissionCheck>
              <GridItem>
                <Button
                  width="full"
                  size="xs"
                  variant="outline"
                  color="fg"
                  borderColor="fg"
                  onClick={() =>
                    handleSubmitPo({
                      purchaseOrder: item,
                      actionButtonType: 'exportPdf',
                    })
                  }
                >
                  Export PDF
                </Button>
              </GridItem>
              <GridItem>
                <Button
                  width="full"
                  size="xs"
                  variant="outline"
                  color="fg"
                  borderColor="fg"
                  onClick={() =>
                    handleSubmitPo({
                      purchaseOrder: item,
                      actionButtonType: 'viewInvoices',
                    })
                  }
                >
                  View Invoices
                </Button>
              </GridItem>
            </Grid>
          );
        }

        return (
          <Grid templateColumns="repeat(2, 1fr)" gap={2}>
            <PermissionCheck permissions={permissionsKeys.change_purchaseorder}>
              {canEditPurchaseOrder(item.status) && (
                <>
                  <GridItem>
                    <Button
                      width="full"
                      size="xs"
                      variant="outline"
                      color="fg"
                      borderColor="fg"
                      onClick={() =>
                        handleSubmitPo({
                          purchaseOrder: item,
                          actionButtonType: 'editPO',
                        })
                      }
                    >
                      Edit PO
                    </Button>
                  </GridItem>

                  <GridItem>
                    <Button
                      width="full"
                      size="xs"
                      variant="outline"
                      color="fg"
                      borderColor="fg"
                      onClick={() =>
                        handleSubmitPo({
                          purchaseOrder: item,
                          actionButtonType: 'editPOLines',
                        })
                      }
                    >
                      Edit PO Lines
                    </Button>
                  </GridItem>
                </>
              )}
              {!canEditPurchaseOrder(item.status) && (
                <GridItem>
                  <Button
                    width="full"
                    size="xs"
                    variant="outline"
                    color="fg"
                    borderColor="fg"
                    onClick={() =>
                      handleSubmitPo({
                        purchaseOrder: item,
                        actionButtonType: 'changePONumber',
                      })
                    }
                  >
                    Change PO Number
                  </Button>
                </GridItem>
              )}

              <GridItem>
                <Button
                  width="full"
                  size="xs"
                  bg="green.800"
                  onClick={() =>
                    handleSubmitPo({
                      purchaseOrder: item,
                      actionButtonType: 'status',
                    })
                  }
                >
                  Status
                </Button>
              </GridItem>

              <GridItem>
                {!canCancelPurchaseOrder(item.status) ? (
                  <PopoverLazy
                    disabled
                    button={
                      <Button width="full" size="xs" bg="red.800">
                        Cancel
                      </Button>
                    }
                    popOverTitle=""
                  >
                    <Flex flexDirection="column" gapY={4}>
                      <Text fontWeight="bold" fontSize="md">
                        {item.status === 'PARTIALLY_RECEIVED'
                          ? 'Receiving has started, so this order cannot be cancelled or deleted.'
                          : 'Cannot cancel this order!'}
                      </Text>
                    </Flex>
                  </PopoverLazy>
                ) : (
                  <Button
                    width="full"
                    size="xs"
                    bg="red.800"
                    onClick={() =>
                      handleSubmitPo({
                        purchaseOrder: item,
                        actionButtonType: 'cancel',
                      })
                    }
                  >
                    Cancel
                  </Button>
                )}
              </GridItem>
            </PermissionCheck>

            <PermissionCheck permissions={permissionsKeys.delete_purchaseorder}>
              {canPermanentlyDeletePurchaseOrder(item.status) && (
                <GridItem>
                  <Button
                    width="full"
                    size="xs"
                    bg="red.900"
                    onClick={() =>
                      handleSubmitPo({
                        purchaseOrder: item,
                        actionButtonType: 'delete',
                      })
                    }
                  >
                    Permanently Delete
                  </Button>
                </GridItem>
              )}
              {mustCancelBeforeDeletingPurchaseOrder(item.status) && (
                <GridItem>
                  <PopoverLazy
                    disabled
                    button={
                      <Button width="full" size="xs" variant="outline">
                        Delete
                      </Button>
                    }
                    popOverTitle=""
                  >
                    <Text fontWeight="semibold">
                      Cancel this purchase order before permanently deleting it.
                    </Text>
                  </PopoverLazy>
                </GridItem>
              )}
            </PermissionCheck>

            <GridItem>
              <Button
                width="full"
                size="xs"
                variant="outline"
                color="fg"
                borderColor="fg"
                onClick={() =>
                  handleSubmitPo({
                    purchaseOrder: item,
                    actionButtonType: 'exportPdf',
                  })
                }
              >
                Export PDF
              </Button>
            </GridItem>
            <GridItem>
              <Button
                width="full"
                size="xs"
                variant="outline"
                color="fg"
                borderColor="fg"
                onClick={() =>
                  handleSubmitPo({
                    purchaseOrder: item,
                    actionButtonType: 'viewInvoices',
                  })
                }
              >
                View Invoices
              </Button>
            </GridItem>
          </Grid>
        );
      }

      default:
        return cellValue?.value ?? cellValue ?? '';
    }
  };

  return (
    <ActionTable.Provider>
      <Center flexDirection="column">
        <ActionTable.FilterBar
          filterConfigComponents={filterConfigComponents}
          disabled={
            isLoadingGetAllPurchaseOrders ||
            isLoadingGetAllUserNames ||
            isLoadingGetAllSuppliers ||
            isLoadingGetAllProjects
          }
        />
        <Flex flexDirection="column" gapY={2}>
          <Box display="flex" flexDirection="row" gap={5}>
            <Box h="100%" my={5}>
              <ActionTable<PurchaseOrdersTableType, { actions: null }>
                width="80vw"
                columns={purchaseOrderTableColumns}
                columnToUseAsRowKey="id"
                items={purchaseOrders}
                isPaginated={false}
                isPaginatedAsync={false}
                totalItemsCount={purchaseOrders.length}
                isSortable={true}
                isFilterable={true}
                isShowActionBar={false}
                height={'55vh'}
                rowHeight={140}
                isLoading={isLoadingGetAllPurchaseOrders}
                isMultipleSelection={false}
                renderCell={renderCell}
              />
            </Box>
          </Box>
        </Flex>
      </Center>
    </ActionTable.Provider>
  );
};
export default PurchaseOrdersTable;

type ActionButtonType =
  | 'editPO'
  | 'changePONumber'
  | 'editPOLines'
  | 'cancel'
  | 'restore'
  | 'delete'
  | 'status'
  | 'exportPdf'
  | 'viewInvoices';

const receivePoSchema = z.object({
  received_date: z
    .string()
    .nonempty({ error: 'Received date is required' })
    .refine((val) => dayjs(val).isValid(), { error: 'Select a valid date.' }),
  received_notes: z.string().optional(),
});
type ReceivePoForm = z.infer<typeof receivePoSchema>;

// A PO is overdue when it is still open (not terminal-received), has no
// recorded received date, and its expected delivery date is in the past.
const isPoOverdue = (po: PurchaseOrdersTableType) => {
  if (
    po?.status === 'RECEIVED' ||
    po?.status === 'CLOSED' ||
    po?.status === 'CANCELLED'
  )
    return false;
  if (po?.received_date) return false;
  const due = po?.expected_delivery_date;
  return Boolean(
    due && dayjs(due).isValid() && dayjs.utc(due).isBefore(dayjs(), 'day'),
  );
};

const purchaseOrderTableColumns: ActionTable.ColumnsConfigType<
  PurchaseOrdersTableType,
  { actions: null }
> = [
  { id: { label: 'Id', width: '20px' } },
  {
    project_po_number: { label: 'Purchase Order Number', width: '110px' },
  },
  { title: { label: 'Title', width: '120px' } },
  { function_number: { label: 'Function Number', width: '100px' } },
  { created_at: { label: 'Created At', width: '90px' } },
  {
    expected_delivery_date: { label: 'Expected Delivery Date', width: '90px' },
  },
  { created_by: { label: 'Created By', width: '70px' } },
  { supplier: { label: 'Supplier', width: '70px' } },
  { project: { label: 'Project', width: '70px' } },
  { release_numbers: { label: 'Releases', width: '100px' } },
  { status: { label: 'Status', width: '80px' } },
  { received_date: { label: 'Received', width: '90px' } },
  { supplier_reference: { label: 'Supplier Quote Number', width: '140px' } },
  { source_draft: { label: 'Source Draft', width: '50px' } },
  { shipping_address: { label: 'Shipping Address', width: '160px' } },
  { actions: { label: 'Actions', width: '220px' } },
];

const purchaseOrderStatusColor = ({
  type,
  status,
}: {
  type: 'button' | 'text';
  status: PurchaseOrderStatusType;
}) => {
  switch (status) {
    case 'Cancelled':
      return type === 'button' ? 'red.700' : 'red.600';
    case 'Draft PO':
      return type === 'button' ? 'gray.700' : 'gray.500';
    case 'Issued':
      return type === 'button' ? 'blue.700' : 'blue.400';
    case 'Received':
      return type === 'button' ? 'orange.800' : 'orange.600';
    case 'Closed':
      return type === 'button' ? 'green.800' : 'green.600';
    default:
      return 'fg';
  }
};
